/**
 * <cd-inquiry-form>
 *
 * Wraps an inquiry-form (rendered by snippets/cd-inquiry-form-fields.liquid)
 * and submits to Klaviyo's public client-side endpoint:
 *
 *   POST https://a.klaviyo.com/client/subscriptions/?company_id=<PUBLIC_KEY>
 *   revision: 2024-10-15
 *
 * Body shape (Klaviyo client-subscription spec):
 *   {
 *     data: {
 *       type: 'subscription',
 *       attributes: {
 *         custom_source: <data-lead-source>,
 *         profile: { data: { type: 'profile', attributes: {
 *           email, phone_number, properties: { ... }
 *         }}}
 *       },
 *       relationships: { list: { data: { type: 'list', id: <LIST_ID> }}}
 *     }
 *   }
 *
 * Config (data-attributes on the host element):
 *   data-public-key       Klaviyo public API key (company_id)
 *   data-list-id          Klaviyo list to subscribe the profile to
 *   data-lead-source      "Team Events Lander" — written to custom_source
 *                         AND included as profile property "Lead Source"
 *   data-redirect-url     post-success redirect (optional)
 *   data-redirect-delay   seconds to wait before redirect (default 3)
 *   data-redirect-enabled "true" | "false" (default true)
 *   data-success-heading  inline success heading
 *   data-success-message  inline success body
 *   data-error-message    inline error body
 *
 * If `public-key` or `list-id` is blank, the component STILL simulates a
 * successful flow so the visual experience is testable. It logs the
 * payload to console and warns once that Klaviyo isn't configured. Once
 * the keys are populated via the theme editor, real submissions go live
 * with no code change.
 */
(function () {
  if (customElements.get('cd-inquiry-form')) return;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';

  /* -------------------------------------------------------------
   * Marketing attribution — first-touch UTM capture
   *
   * Mirrors how Klaviyo's onsite JS handles attribution on the
   * live www.paintingtogogh.com pages: read utm_* params on first
   * landing, persist in a first-party cookie for 30 days, and
   * preserve them across visits so the form submission carries the
   * ORIGINAL acquisition source even if the visitor returned later
   * via a different path.
   *
   * Form payload (in buildPayload below) sends:
   *   utm_source / utm_medium / utm_campaign / utm_term / utm_content
   *     (latest touch from URL OR cookie fallback)
   *   $attribution_source / $attribution_medium / etc.
   *     (first-touch from cookie — Klaviyo's standard names)
   *   leadsource
   *     (utm_source if present, else mapped referrer like "Google",
   *      else "Direct")
   *   Landing Page / Referrer
   *     (URL + referrer of the FIRST touch, for sales context)
   *
   * Empty values are stripped before send so profiles don't end up
   * with `$attribution_term: ""` properties cluttering the data.
   * ------------------------------------------------------------- */
  var ATTR_COOKIE = 'cd_attribution';
  var ATTR_DAYS = 30;
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  function readAttrCookie() {
    try {
      var prefix = ATTR_COOKIE + '=';
      var parts = (document.cookie || '').split('; ');
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(prefix) === 0) {
          var raw = decodeURIComponent(parts[i].substring(prefix.length));
          var parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') return parsed;
        }
      }
    } catch (_) {}
    return null;
  }

  function writeAttrCookie(data) {
    try {
      var expires = new Date();
      expires.setTime(expires.getTime() + ATTR_DAYS * 24 * 60 * 60 * 1000);
      var raw = encodeURIComponent(JSON.stringify(data));
      document.cookie = ATTR_COOKIE + '=' + raw + '; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax';
    } catch (_) {}
  }

  function utmsFromURL() {
    try {
      var params = new URLSearchParams(window.location.search);
      var found = {};
      var any = false;
      for (var i = 0; i < UTM_KEYS.length; i++) {
        var v = params.get(UTM_KEYS[i]);
        if (v) { found[UTM_KEYS[i]] = v; any = true; }
      }
      return any ? found : null;
    } catch (_) { return null; }
  }

  function referrerHost() {
    try {
      var ref = document.referrer || '';
      if (!ref) return '';
      return new URL(ref).hostname.replace(/^www\./, '');
    } catch (_) { return ''; }
  }

  /**
   * Map a referrer host (or utm_source value) to a normalized
   * leadsource label. Mirrors Klaviyo's common attribution buckets.
   */
  function normalizeLeadSource(value) {
    if (!value) return '';
    var v = String(value).toLowerCase();
    var known = {
      'google': 'Google', 'google.com': 'Google',
      'bing': 'Bing', 'bing.com': 'Bing',
      'duckduckgo': 'DuckDuckGo', 'duckduckgo.com': 'DuckDuckGo',
      'facebook': 'Facebook', 'facebook.com': 'Facebook', 'm.facebook.com': 'Facebook', 'l.facebook.com': 'Facebook',
      'instagram': 'Instagram', 'instagram.com': 'Instagram',
      'linkedin': 'LinkedIn', 'linkedin.com': 'LinkedIn',
      'twitter': 'Twitter', 'twitter.com': 'Twitter', 'x.com': 'Twitter',
      'tiktok': 'TikTok', 'tiktok.com': 'TikTok',
      'pinterest': 'Pinterest', 'pinterest.com': 'Pinterest',
      'youtube': 'YouTube', 'youtube.com': 'YouTube',
      'reddit': 'Reddit', 'reddit.com': 'Reddit',
      'klaviyo': 'Klaviyo', 'email': 'Email',
      'newsletter': 'Newsletter'
    };
    if (known[v]) return known[v];
    // Subdomain match (e.g. lm.facebook.com → Facebook)
    for (var k in known) {
      if (v.endsWith('.' + k)) return known[k];
    }
    // Capitalize first letter as a default ("cold-email" stays "cold-email")
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /* Run once on module load: if URL has UTMs and we don't already
     have a first-touch cookie, write it. If no UTMs but we have a
     non-empty referrer, capture that as the first-touch context.
     Existing cookies are never overwritten — first-touch wins. */
  (function captureFirstTouch() {
    if (readAttrCookie()) return; // already captured, preserve

    var fromURL = utmsFromURL();
    var ref = document.referrer || '';
    var refHost = referrerHost();

    if (fromURL || ref) {
      var data = fromURL || {};
      data._referrer = ref;
      data._referrer_host = refHost;
      data._landing_page = window.location.href;
      data._captured_at = new Date().toISOString();
      writeAttrCookie(data);
    }
  })();

  /**
   * Best-effort E.164 normalizer. Klaviyo accepts E.164 phone numbers
   * (+15555550100). Strip every non-digit; if 10 digits, prefix +1; if
   * already 11 digits starting with 1, prefix +; otherwise return null
   * so the caller can fall back to storing the raw value as a property.
   */
  function toE164(raw) {
    if (!raw) return null;
    var digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    if (digits.length >= 8 && String(raw).charAt(0) === '+') return '+' + digits;
    return null;
  }

  /**
   * Split "First Last" → ["First", "Last (rest)"]. If there's no space,
   * everything goes into First. Klaviyo uses separate first/last on
   * profile properties; we keep the split simple, no titlecasing.
   */
  function splitName(full) {
    if (!full) return { first: '', last: '' };
    var parts = String(full).trim().split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  }

  class CDInquiryForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.errorEl = this.querySelector('[data-cd-inq-error]');
      this.submitBtn = this.querySelector('[data-cd-inq-submit]');
      this.successEl = this.querySelector('[data-cd-inq-success]');

      // Cache config off data-attributes so we don't re-read the DOM
      // on every submit.
      this.publicKey = this.getAttribute('data-public-key') || '';
      this.listId = this.getAttribute('data-list-id') || '';
      this.leadSource = this.getAttribute('data-lead-source') || 'Inquiry Form';
      this.redirectUrl = this.getAttribute('data-redirect-url') || '';
      this.redirectDelay = parseInt(this.getAttribute('data-redirect-delay'), 10);
      if (isNaN(this.redirectDelay) || this.redirectDelay < 0) this.redirectDelay = 3;
      this.redirectEnabled = this.getAttribute('data-redirect-enabled') !== 'false';
      this.errorMessage = this.getAttribute('data-error-message')
        || 'Something went wrong. Please try again or call us at 312-380-1206.';

      // Spam guard — record when the form was wired up. onSubmit
      // compares against this; submissions in the first few seconds
      // are treated as bots. Real users take longer than ~3s to fill
      // even the shortest variant of this form.
      this.connectedAt = Date.now();

      this._onSubmitBound = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this._onSubmitBound);

      // Track form view to Klaviyo so we can compute view→submit
      // conversion. IntersectionObserver fires once when ≥50% of the
      // form is on screen — counting only views that actually saw
      // the form, not page loads where the user never scrolled to it.
      this._viewTracked = false;
      if (typeof IntersectionObserver !== 'undefined') {
        var self = this;
        this._viewObserver = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
              self._trackKlaviyo('Viewed Team Events Form', {
                lead_source: self.leadSource,
                page_url: window.location && window.location.href || '',
                page_path: window.location && window.location.pathname || ''
              });
              self._viewTracked = true;
              self._viewObserver.disconnect();
              break;
            }
          }
        }, { threshold: 0.5 });
        this._viewObserver.observe(this);
      }
    }

    /**
     * Push a track event to Klaviyo's onsite JS queue. _learnq is
     * created by the Klaviyo Onsite Javascript app embed; if it's
     * missing (app embed disabled) the call is a no-op. Use this for
     * funnel metrics — Klaviyo turns these into named metrics under
     * Analytics → Metrics so you can build conversion-rate reports
     * (Submitted / Viewed).
     */
    _trackKlaviyo(eventName, properties) {
      try {
        if (typeof window === 'undefined') return;
        window._learnq = window._learnq || [];
        window._learnq.push(['track', eventName, properties || {}]);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[cd-inquiry-form] track failed:', eventName, err);
        }
      }
    }

    disconnectedCallback() {
      if (this.form && this._onSubmitBound) {
        this.form.removeEventListener('submit', this._onSubmitBound);
      }
      if (this._viewObserver) {
        this._viewObserver.disconnect();
        this._viewObserver = null;
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      if (message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    setSubmitting(isSubmitting) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = isSubmitting;
      this.submitBtn.classList.toggle('is-submitting', isSubmitting);
    }

    /**
     * Replace the form with the success state. Caller has already
     * collected the form data; this only handles UI swap + redirect.
     */
    showSuccess() {
      if (this.successEl) {
        this.successEl.hidden = false;
        // Move focus to the success heading so screen readers announce it.
        var heading = this.successEl.querySelector('h2, h3, [data-cd-inq-success-heading]');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: false });
        }
      }
      if (this.form) this.form.hidden = true;

      if (this.redirectEnabled && this.redirectUrl) {
        var self = this;
        setTimeout(function () {
          window.location.href = self.redirectUrl;
        }, this.redirectDelay * 1000);
      }
    }

    /**
     * Build the attribution-property bag for Klaviyo. Combines:
     *   - Latest-touch utm_* from URL (overrides cookie if present)
     *   - First-touch $attribution_* from the persisted cookie
     *   - Derived `leadsource` (Klaviyo's go-to attribution label)
     *   - Landing Page + Referrer for sales context
     *
     * Empty values are stripped so we don't send `utm_term: ""` etc.
     * Result is spread into the profile properties bag in
     * buildPayload() — caller doesn't need to know the keys.
     */
    buildAttributionProperties() {
      var firstTouch = readAttrCookie() || {};
      var current = utmsFromURL() || {};

      // leadsource priority: live utm_source → first-touch utm_source
      // → mapped referrer host → 'Direct'
      var leadsource = normalizeLeadSource(current.utm_source)
        || normalizeLeadSource(firstTouch.utm_source)
        || normalizeLeadSource(firstTouch._referrer_host || referrerHost())
        || 'Direct';

      var props = {
        'leadsource': leadsource
      };

      // Latest-touch utm_* (URL preferred, cookie fallback). Klaviyo
      // segments and reports often look at flat utm_* property names.
      for (var i = 0; i < UTM_KEYS.length; i++) {
        var k = UTM_KEYS[i];
        var v = current[k] || firstTouch[k] || '';
        if (v) props[k] = v;
      }

      // First-touch $attribution_* — Klaviyo's standard property names
      // for marketing attribution. These NEVER overwrite once captured.
      var attrMap = {
        utm_source: '$attribution_source',
        utm_medium: '$attribution_medium',
        utm_campaign: '$attribution_campaign',
        utm_term: '$attribution_term',
        utm_content: '$attribution_content'
      };
      for (var ftKey in attrMap) {
        var ftVal = firstTouch[ftKey];
        if (ftVal) props[attrMap[ftKey]] = ftVal;
      }

      // Sales context: where they actually arrived.
      if (firstTouch._landing_page) props['Landing Page'] = firstTouch._landing_page;
      if (firstTouch._referrer) props['Referrer'] = firstTouch._referrer;

      return props;
    }

    /**
     * Build the Klaviyo subscription payload from current form values.
     * Property names match the user's Klaviyo profile schema:
     *   $phone_number_region, $source         (system properties)
     *   Additional Info, Event Date, Event Size,
     *   Event Type [array], Shipping Location [array], leadsource
     * Plus First/Last Name + Company for profile-level attribution.
     */
    buildPayload() {
      var fd = new FormData(this.form);
      var name = (fd.get('first_last_name') || '').toString().trim();
      var company = (fd.get('company') || '').toString().trim();
      var email = (fd.get('email') || '').toString().trim();
      var phoneRaw = (fd.get('phone') || '').toString().trim();
      var groupSize = (fd.get('group_size') || '').toString().trim();
      var eventType = (fd.get('event_type') || '').toString().trim();
      var formatPreference = (fd.get('format_preference') || '').toString().trim();
      var eventDate = (fd.get('event_date') || '').toString().trim();
      var notes = (fd.get('notes') || '').toString().trim();
      // "How did you hear about us?" dropdown — maps to the Klaviyo "Lead
      // Source" profile property (the dropdown value IS the lead source).
      var heardFrom = (fd.get('heard_from') || '').toString().trim();
      // Suggest-a-painting variant fields. Absent on team-events forms;
      // fd.get returns null, fallbacks to '' so empty values are stripped
      // a few lines below alongside other empty properties.
      var paintIdea = (fd.get('paint_idea') || '').toString().trim();
      var styleVibe = (fd.get('style_vibe') || '').toString().trim();
      var difficulty = (fd.get('difficulty') || '').toString().trim();
      var inspiration = (fd.get('inspiration') || '').toString().trim();
      var wouldBuyKit = (fd.get('would_buy_kit') || '').toString().trim();

      if (!name || !email) return null;

      var split = splitName(name);
      var phoneE164 = toE164(phoneRaw);

      var properties = {
        // Profile attribution
        'First Name': split.first,
        'Last Name': split.last,
        'Company': company,
        // User's Klaviyo schema (matches existing customer profiles).
        // NOTE: $source is set via the `custom_source` field at the
        // subscription level below — NOT duplicated here. Klaviyo's
        // Client Subscriptions API rejects most $-prefixed system
        // properties when set inside the profile properties bag.
        // $phone_number_region is implied by the E.164 phone (the
        // `+1` prefix encodes US), so no separate property needed.
        'Additional Info': notes,
        // The "How did you hear about us?" dropdown selection, stored as the
        // Klaviyo "Lead Source" custom property. Stripped below if left blank
        // (the field is optional). Distinct from the UTM-derived `leadsource`
        // attribution bucket added by buildAttributionProperties().
        'Lead Source': heardFrom,
        'Event Date': eventDate,
        'Event Size': groupSize ? parseInt(groupSize, 10) : null,
        // Klaviyo `Event Type` and `Shipping Location` are array
        // properties on existing profiles. Wrapping each single-
        // select value in a one-element array matches the shape so
        // segments + flows that filter on these properties work.
        // (Form fields stay single-select; JS does the array wrap.)
        'Event Type': formatPreference ? [formatPreference] : null,
        'Shipping Location': eventType ? [eventType] : null,
        // Suggest-a-painting properties. Distinct Klaviyo names so the
        // owner can segment suggestion submissions separately from
        // corporate quote leads.
        'Painting Idea': paintIdea,
        'Style Vibe': styleVibe,
        'Difficulty': difficulty,
        'Inspiration Link': inspiration,
        'Would Buy Kit': wouldBuyKit
      };
      // Drop empty / null entries so Klaviyo doesn't reject "" values.
      for (var pk in properties) {
        if (properties.hasOwnProperty(pk)) {
          var pv = properties[pk];
          if (pv === '' || pv === null || pv === undefined) delete properties[pk];
        }
      }

      // Spread the attribution bag (utm_*, $attribution_*, leadsource,
      // Landing Page, Referrer). Built from a 30-day first-touch
      // cookie + latest-touch URL params. Mirrors Klaviyo onsite
      // SDK's first-touch-wins behavior — see the module-level
      // captureFirstTouch IIFE above.
      var attributionProps = this.buildAttributionProperties();
      for (var attrKey in attributionProps) {
        if (attributionProps.hasOwnProperty(attrKey) && attributionProps[attrKey] !== '') {
          properties[attrKey] = attributionProps[attrKey];
        }
      }

      // If phone didn't normalize, fall back to a raw property so we
      // still capture the lead instead of failing the whole submission.
      if (!phoneE164) properties['Phone (raw)'] = phoneRaw;

      var profileAttrs = {
        email: email,
        properties: properties
      };
      if (phoneE164) profileAttrs.phone_number = phoneE164;

      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.leadSource,
            profile: {
              data: { type: 'profile', attributes: profileAttrs }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } }
          }
        }
      };
    }

    async onSubmit(e) {
      e.preventDefault();
      this.setError('');

      // ---- Spam guards ----------------------------------------------
      // Both checks "fake success" instead of erroring. A real bot
      // sees a clean UX and moves on; an error would tell it to retry
      // with different inputs until it gets through.
      //
      // 1) Honeypot — invisible field cd-inquiry-form-fields.liquid
      //    renders inside the form. Humans never see it; bots that
      //    auto-fill every input will set a value.
      var honeypot = this.form.querySelector('[data-cd-inq-honeypot]');
      if (honeypot && honeypot.value && honeypot.value.trim() !== '') {
        if (window.console && console.warn) {
          console.warn('[cd-inquiry-form] Honeypot tripped, dropping submission.');
        }
        this.setSubmitting(true);
        var honeyTimer = this;
        setTimeout(function () {
          honeyTimer.setSubmitting(false);
          honeyTimer.showSuccess();
        }, 600);
        return;
      }

      // 2) Time-trap — bots fire instantly after page load; humans
      //    take 5-30s to fill the form. Reject anything under 3s.
      var elapsed = Date.now() - (this.connectedAt || 0);
      if (elapsed < 3000) {
        if (window.console && console.warn) {
          console.warn('[cd-inquiry-form] Time-trap tripped (' + elapsed + 'ms), dropping submission.');
        }
        this.setSubmitting(true);
        var trapTimer = this;
        setTimeout(function () {
          trapTimer.setSubmitting(false);
          trapTimer.showSuccess();
        }, 600);
        return;
      }
      // ---------------------------------------------------------------

      var payload = this.buildPayload();
      if (!payload) {
        // Native HTML5 required validation should have caught this;
        // re-trigger reportValidity() so the user sees the right field.
        if (this.form && typeof this.form.reportValidity === 'function') {
          this.form.reportValidity();
        }
        return;
      }

      // Empty keys = simulate-success mode. Logs the payload, warns
      // once, runs the success UI flow so the visual path is testable
      // before Klaviyo is configured.
      if (!this.publicKey || !this.listId) {
        if (window.console) {
          if (!CDInquiryForm._warnedNoKeys) {
            console.warn('[cd-inquiry-form] Klaviyo not configured (public_key or list_id empty). Form will simulate success without submitting.');
            CDInquiryForm._warnedNoKeys = true;
          }
          console.info('[cd-inquiry-form] Simulated payload:', JSON.parse(JSON.stringify(payload)));
        }
        this.setSubmitting(true);
        var self = this;
        setTimeout(function () {
          self.setSubmitting(false);
          self.showSuccess();
        }, 350);
        return;
      }

      this.setSubmitting(true);
      try {
        var url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(this.publicKey);
        var res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        });
        // Klaviyo returns 202 Accepted on success for this endpoint.
        // Anything in the 2xx range is treated as success; everything
        // else falls through to the catch / error message branch.
        if (!res.ok) {
          var bodyText = '';
          try { bodyText = await res.text(); } catch (_) {}
          throw new Error('Klaviyo HTTP ' + res.status + (bodyText ? ': ' + bodyText.slice(0, 200) : ''));
        }

        // Link the just-created profile to the anonymous klaviyo.js
        // cookie session. Without this call, the profile we just
        // posted to /client/subscriptions is a new identity unrelated
        // to the session that captured the UTMs in __kla_id — so
        // Klaviyo's "How they found you" panel stays blank.
        // _learnq is the queue klaviyo.js (Onsite JS app embed) sets
        // up; pushing 'identify' attaches the cookie attribution data
        // (utm_source/medium/campaign/content/term, referrer, landing
        // page) to the profile matching this email.
        try {
          var profileEmail = (payload
            && payload.data
            && payload.data.attributes
            && payload.data.attributes.profile
            && payload.data.attributes.profile.data
            && payload.data.attributes.profile.data.attributes
            && payload.data.attributes.profile.data.attributes.email) || '';
          if (profileEmail && typeof window !== 'undefined') {
            window._learnq = window._learnq || [];
            var idTraits = { '$email': profileEmail };
            // Carry first/last name into identify if we have them so
            // klaviyo.js can use them for the session attribution.
            var profileAttrs = payload.data.attributes.profile.data.attributes || {};
            if (profileAttrs.first_name) idTraits.$first_name = profileAttrs.first_name;
            if (profileAttrs.last_name) idTraits.$last_name = profileAttrs.last_name;
            window._learnq.push(['identify', idTraits]);
          }
        } catch (idErr) {
          if (window.console && console.warn) {
            console.warn('[cd-inquiry-form] _learnq identify failed:', idErr);
          }
        }

        // Track submit to Klaviyo so we get a real conversion-rate
        // metric. Pair with the "Viewed Team Events Form" event
        // fired when the form first scrolls into view; ratio of the
        // two is the form's true view→submit rate. This is also the
        // event a Klaviyo Flow triggers off to post into Slack.
        this._trackKlaviyo('Submitted Team Events Form', {
          lead_source: this.leadSource,
          page_url: window.location && window.location.href || '',
          page_path: window.location && window.location.pathname || ''
        });

        this.setSubmitting(false);
        this.showSuccess();
      } catch (err) {
        if (window.console && console.warn) console.warn('[cd-inquiry-form] submit failed:', err);
        this.setSubmitting(false);
        this.setError(this.errorMessage);
      }
    }
  }

  customElements.define('cd-inquiry-form', CDInquiryForm);
})();
